import React from 'react';
import { FiSearch } from 'react-icons/fi';
import ApiService from '../services/ApiService';
import '../styles/ChatList.css';

// Updated: 2026-02-11 - Fixed key prop
function ChatList({
  chats,
  selectedChat,
  onSelectChat,
  onDeleteDialogRequest,
  onMarkReadRequest,
  onMarkUnreadRequest,
  canManageReadState = false,
  loading,
  showOperatorFilter = false
}) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedOperatorId, setSelectedOperatorId] = React.useState('');
  const [contextMenu, setContextMenu] = React.useState({ visible: false, x: 0, y: 0, chat: null });
  const [matchedByMessages, setMatchedByMessages] = React.useState(() => new Set());
  const searchRequestIdRef = React.useRef(0);
  const contextMenuRef = React.useRef(null);

  const normalize = React.useCallback((value) => String(value || '').toLowerCase(), []);

  const operatorOptions = React.useMemo(() => {
    const map = new Map();
    (Array.isArray(chats) ? chats : []).forEach((chat) => {
      const operators = Array.isArray(chat?.operators) ? chat.operators : [];
      operators.forEach((op) => {
        const userId = Number(op?.userId);
        if (!Number.isFinite(userId) || userId <= 0) return;
        if (map.has(userId)) return;
        const displayName = String(op?.displayName || '').trim();
        const username = String(op?.username || '').trim();
        const label = displayName || username || `ID ${userId}`;
        map.set(userId, { userId, label });
      });
    });

    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, 'ru'));
  }, [chats]);

  const chatsByOperator = React.useMemo(() => {
    if (!showOperatorFilter) return chats;
    if (!selectedOperatorId) return chats;
    const selected = Number(selectedOperatorId);
    if (!Number.isFinite(selected) || selected <= 0) return chats;
    return chats.filter((chat) => hasOperator(chat, selected));
  }, [chats, selectedOperatorId, showOperatorFilter]);

  React.useEffect(() => {
    if (!showOperatorFilter && selectedOperatorId) {
      setSelectedOperatorId('');
    }
  }, [showOperatorFilter, selectedOperatorId]);

  const localMatchMap = React.useMemo(() => {
    const term = normalize(searchTerm).trim();
    const map = new Map();

    chatsByOperator.forEach((chat) => {
      const fullName = `${chat.firstName || ''} ${chat.lastName || ''}`.trim();
      const previewText = getChatPreview(chat);
      const isMatch = !term
        || normalize(fullName).includes(term)
        || normalize(previewText).includes(term);
      map.set(chat.chatId, isMatch);
    });

    return map;
  }, [chatsByOperator, normalize, searchTerm]);

  React.useEffect(() => {
    const term = normalize(searchTerm).trim();

    if (!term) {
      setMatchedByMessages(new Set());
      return undefined;
    }

    const chatsForRemoteSearch = chatsByOperator.filter((chat) => !localMatchMap.get(chat.chatId));
    if (chatsForRemoteSearch.length === 0) {
      setMatchedByMessages(new Set());
      return undefined;
    }

    const requestId = ++searchRequestIdRef.current;
    const timer = setTimeout(async () => {
      const matchedIds = new Set();

      await Promise.all(chatsForRemoteSearch.map(async (chat) => {
        try {
          const result = await ApiService.searchMessages(chat.chatId, term);
          if (Array.isArray(result) && result.length > 0) {
            matchedIds.add(chat.chatId);
          }
        } catch {
          // ignore per-chat search errors
        }
      }));

      if (searchRequestIdRef.current !== requestId) return;
      setMatchedByMessages(matchedIds);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [chatsByOperator, localMatchMap, normalize, searchTerm]);

  const filteredChats = chatsByOperator.filter((chat) => {
    const fullName = `${chat.firstName || ''} ${chat.lastName || ''}`.trim();
    const previewText = getChatPreview(chat);
    const term = normalize(searchTerm).trim();

    if (!term) return true;

    if (normalize(fullName).includes(term) || normalize(previewText).includes(term)) {
      return true;
    }

    return matchedByMessages.has(chat.chatId);
  });

  React.useEffect(() => {
    if (!contextMenu.visible) return undefined;

    const onDocClick = (e) => {
      if (contextMenuRef.current && contextMenuRef.current.contains(e.target)) return;
      setContextMenu({ visible: false, x: 0, y: 0, chat: null });
    };

    const onEsc = (e) => {
      if (e.key === 'Escape') {
        setContextMenu({ visible: false, x: 0, y: 0, chat: null });
      }
    };

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [contextMenu.visible]);

  const openChatContextMenu = (e, chat) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      chat
    });
  };

  const handleDeleteDialogClick = () => {
    const chat = contextMenu.chat;
    setContextMenu({ visible: false, x: 0, y: 0, chat: null });
    if (!chat || !onDeleteDialogRequest) return;
    onDeleteDialogRequest(chat);
  };

  const handleMarkUnreadClick = () => {
    const chat = contextMenu.chat;
    setContextMenu({ visible: false, x: 0, y: 0, chat: null });
    if (!chat || !onMarkUnreadRequest) return;
    onMarkUnreadRequest(chat);
  };

  const handleMarkReadClick = () => {
    const chat = contextMenu.chat;
    setContextMenu({ visible: false, x: 0, y: 0, chat: null });
    if (!chat || !onMarkReadRequest) return;
    onMarkReadRequest(chat);
  };

  if (loading) {
    return (
      <div className="chat-list">
        <div className="chat-list-header">
          <h1>KAT-Telecom MAX Messenger</h1>
        </div>
        <div className="loading">Загрузка чатов...</div>
      </div>
    );
  }

  return (
    <div className="chat-list">
      <div className="chat-list-header">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Поиск..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {showOperatorFilter && (
          <div className="chat-operator-filter">
            <select
              value={selectedOperatorId}
              onChange={(e) => setSelectedOperatorId(e.target.value)}
            >
              <option value="">Все сотрудники</option>
              {operatorOptions.map((op) => (
                <option key={op.userId} value={op.userId}>
                  {op.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="chats">
        {filteredChats.length === 0 ? (
          <div className="no-chats">Нет чатов</div>
        ) : (
          filteredChats.map((chat) => {
            const previewText = getChatPreview(chat);
            const unreadCount = Number(chat?.unreadCount || 0);
            return (
              <div
                key={chat.chatId || chat.id}
                className={`chat-item ${selectedChat?.chatId === chat.chatId ? 'active' : ''}`}
                onClick={() => onSelectChat(chat)}
                onContextMenu={(e) => openChatContextMenu(e, chat)}
              >
                <div className="chat-avatar">
                  {chat.icon?.url ? (
                    <img src={chat.icon.url} alt="Avatar" />
                  ) : (
                    (chat.firstName?.charAt(0) || chat.lastName?.charAt(0) || 'U').toUpperCase()
                  )}
                </div>
                <div className="chat-info">
                  <div className="chat-header">
                    <span className="chat-name">
                      {`${chat.firstName || ''} ${chat.lastName || ''}`.trim()}
                      {chat.isMiniAppAuthorized && (
                        <span className="chat-miniapp-star" title="Авторизован в miniapp" aria-label="Авторизован в miniapp">★</span>
                      )}
                      {chat.isMiniAppAuthorized && chat.miniAppLogin && (
                        <span className="chat-miniapp-login" title="MiniApp логин">{chat.miniAppLogin}</span>
                      )}
                      {hasDepartment(chat, 1) && (
                        <span className="chat-department-badge" title="В диалоге есть сотрудник техподдержки" aria-label="Техподдержка">🛠️</span>
                      )}
                      {hasDepartment(chat, 2) && (
                        <span className="chat-department-badge" title="В диалоге есть сотрудник абонентского отдела" aria-label="Абонентский отдел">💵</span>
                      )}
                    </span>
                    <div className="chat-meta">
                      <span className="chat-time">
                        {formatChatTime(chat.lastMessageTime ?? chat.last_message_time)}
                      </span>
                      {unreadCount > 0 && (
                        <span className="chat-unread-badge">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={`chat-preview ${unreadCount > 0 ? 'unread' : ''}`}>{previewText}</div>
                </div>
              </div>
            );
          })
        )}
      </div>
      {contextMenu.visible && (
        <div
          ref={contextMenuRef}
          className="chat-list-context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          {canManageReadState && (
            <>
              <button type="button" className="chat-list-context-item" onClick={handleMarkUnreadClick}>
                Непрочитанное
              </button>
              <button type="button" className="chat-list-context-item" onClick={handleMarkReadClick}>
                Прочитать
              </button>
            </>
          )}
          <button type="button" className="chat-list-context-item danger" onClick={handleDeleteDialogClick}>
            Удалить диалог
          </button>
        </div>
      )}
    </div>
  );
}

function formatChatTime(rawValue) {
  const date = parseChatDate(rawValue);
  if (!date) return '';
  return formatTime(date);
}

function parseChatDate(rawValue) {
  if (!rawValue && rawValue !== 0) return null;

  if (rawValue instanceof Date) {
    return Number.isNaN(rawValue.getTime()) ? null : rawValue;
  }

  if (typeof rawValue === 'number') {
    // Accept both unix seconds and unix milliseconds.
    const ms = rawValue < 1e12 ? rawValue * 1000 : rawValue;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim();
    if (!trimmed) return null;

    // Numeric string timestamp support.
    if (/^\d+$/.test(trimmed)) {
      return parseChatDate(Number(trimmed));
    }

    // If backend sends ISO without timezone, treat as UTC to avoid local-time drift.
    const needsUtcSuffix = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(trimmed);
    const normalized = needsUtcSuffix ? `${trimmed}Z` : trimmed;

    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

function formatTime(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }

  const now = new Date();
  const diff = now - date;

  if (diff < 0) {
    return date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Сейчас';
  if (minutes < 60) return `${minutes}м`;
  if (hours < 24) return `${hours}ч`;
  if (days < 7) return `${days}д`;

  return date.toLocaleDateString('ru-RU');
}

function getPreviewText(value) {
  if (!value) return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${value}</div>`, 'text/html');
  return (doc.body.textContent || '').replace(/\u00A0/g, ' ').trim();
}

function getChatPreview(chat) {
  const raw = chat?.lastMessage ?? chat?.last_message;
  const text = getPreviewText(raw);
  if (text) {
    if (text === '[Пустое сообщение]') {
      return resolveAttachmentLabel(raw) || 'Вложение';
    }
    return text;
  }

  const hasLastActivity = Boolean(chat?.lastMessageTime ?? chat?.last_message_time);
  if (!hasLastActivity) {
    return 'Нет сообщений';
  }

  return resolveAttachmentLabel(raw) || 'Вложение';
}

function resolveAttachmentLabel(raw) {
  const value = String(raw || '').toLowerCase();
  if (!value) return '';
  if (value.includes('audio') || value.includes('voice') || value.includes('голос')) return '🎤 Голосовое';
  if (value.includes('video')) return '🎬 Видео';
  if (value.includes('image') || value.includes('photo') || value.includes('фото')) return '🖼 Фото';
  if (value.includes('file') || value.includes('документ')) return '📄 Файл';
  if (value.includes('влож')) return '📎 Вложение';
  return '';
}

function hasDepartment(chat, departmentId) {
  if (!chat) return false;
  if (departmentId === 1 && chat.hasDepartment1 === true) return true;
  if (departmentId === 2 && chat.hasDepartment2 === true) return true;
  const ids = Array.isArray(chat.departmentIds) ? chat.departmentIds : [];
  return ids.some((x) => Number(x) === Number(departmentId));
}

function hasOperator(chat, userId) {
  if (!chat) return false;
  const operators = Array.isArray(chat.operators) ? chat.operators : [];
  return operators.some((op) => Number(op?.userId) === Number(userId));
}

export default ChatList;
